import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
    createAsset,
    updateAsset,
    getAssets,
    OWNERSHIP_MODE,
} from "../../services/assets/assetService";

import {
    HOLDER_TYPES
} from "../../constants/holderTypes";

import {
    getResolvedHolderLabel
} from "../../utils/holderResolver";

import {
    collection,
    getDocs,
    query,
    where,
} from "firebase/firestore";

import { db } from "../../firebase";
import {
    createHolderSnapshot
} from "../../utils/holderSnapshotFactory";

export default function AssetMaster() {

    const location = useLocation();
    const navigate = useNavigate();

    // ==================================================
    // STATE
    // ==================================================

    const [loading, setLoading]         = useState(false);
    const [saving, setSaving]           = useState(false);
    const [assets, setAssets]           = useState([]);
    const [siteOptions, setSiteOptions] = useState([]);
    const [staffOptions, setStaffOptions] = useState([]);
    const [editingAsset, setEditingAsset] = useState(null);
    const [error, setError]             = useState("");
    const [success, setSuccess]         = useState("");

    // ── pre-fill banner (from Item Request) ──
    const [fromItemRequest, setFromItemRequest] = useState(false);

    const ASSET_CATALOG = {
        "Power Tool":        ["Drill Machine", "Angle Grinder", "Cutting Machine", "Blower"],
        "Testing Tool":      ["Manifold Gauge", "Clamp Meter", "Multimeter", "Vacuum Gauge", "Leak Detector"],
        "Gas Equipment":     ["Nitrogen Cylinder", "Oxygen Cylinder", "R410A Cylinder"],
        "Safety Equipment":  ["Helmet", "Safety Belt", "Safety Gloves"],
        "Installation Tool": ["Flaring Tool", "Copper Cutter", "Vacuum Pump"],
        "Machine":           ["Core Cutting Machine", "Welding Machine"],
        "Other":             ["Other"],
    };

    const CATEGORY_KEYWORDS = {
        "Power Tool":        ["drill", "grinder", "cutter", "blower", "machine", "saw", "wrench", "spanner", "screwdriver", "hammer", "plier", "bender", "bend", "crimper", "crimping", "press"],
        "Testing Tool":      ["gauge", "manifold", "meter", "tester", "detector", "scanner", "thermometer", "clamp"],
        "Gas Equipment":     ["cylinder", "nitrogen", "gas", "oxygen", "refrigerant", "r410", "r22", "r32"],
        "Safety Equipment":  ["helmet", "belt", "gloves", "safety", "harness", "goggles"],
        "Installation Tool": ["flaring", "flare", "vacuum pump", "pipe cutter", "copper cutter", "reamer", "deburr"],
        "Ladder":            ["ladder", "scaffold", "step"],
        "Machine":           ["vacuum", "pump", "compressor", "welding", "cutting machine", "core cutting"],
    };

    const detectCategory = (assetName) => {
        if (!assetName) return "";
        const lower = assetName.toLowerCase();
        for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
            if (keywords.some(word => lower.includes(word))) return category;
        }
        return "";
    };

    const [formData, setFormData] = useState({
        asset_name:          "",
        asset_category:      "",
        asset_specification: "",
        ownership_mode:      OWNERSHIP_MODE.RETURNABLE,
        serial_no:           "",
        current_holder_type: HOLDER_TYPES.STORE,
        current_holder_id:   "MAIN_STORE",
        remarks:             "",
        condition:           "WORKING",
    });

    // ==================================================
    // USER / COMPANY
    // ==================================================

    const user       = JSON.parse(localStorage.getItem("user") || "{}");
    const company_id = localStorage.getItem("company_id") || user?.company_id;

    // ==================================================
    // PRE-FILL FROM ITEM REQUEST (on mount)
    // ==================================================

    useEffect(() => {
        const state = location.state;
        if (state?.from_item_request && state?.prefill_name) {
            const autoCategory = detectCategory(state.prefill_name);
            const autoSerial   = `AST-${Date.now().toString().slice(-4)}`;
            setFormData(prev => ({
                ...prev,
                asset_name:     state.prefill_name,
                asset_category: autoCategory || "",
                serial_no:      autoSerial,
                remarks:        `Added from Item Request — requested by ${state.requested_by || "technician"}`,
            }));
            setFromItemRequest(true);
            // Clear location state so refresh doesn't re-prefill
            window.history.replaceState({}, document.title);
        }
    }, []);

    // ==================================================
    // LOAD ASSETS
    // ==================================================

    const loadAssets = async () => {
        try {
            setLoading(true);
            const response = await getAssets({ company_id });
            setAssets(response || []);
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load assets");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAssets();
        loadSites();
    }, []);

    // ==================================================
    // LOAD SITES + STAFF
    // ==================================================

    const loadSites = async () => {
        try {
            const siteSnap = await getDocs(query(collection(db, "sites"), where("company_id", "==", company_id)));
            const allSites = [];
            siteSnap.forEach(doc => {
                const site = doc.data();
                allSites.push({
                    id:           doc.id,
                    site_name:    site.site_name || "",
                    address:      site.address   || "",
                    project_id:   site.project_id  || "",
                    client_id:    site.client_id   || "",
                    display_name: site.site_name   || "",
                });
            });
            setSiteOptions(allSites);

            const staffSnap = await getDocs(
                query(collection(db, "users"), where("company_id", "==", company_id))
            );
            const allStaff = staffSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(u => ["technician","staff","sm","manager","store"].includes((u.role||"").toLowerCase().trim()))
                .map(u => ({
                    id:   u.id,
                    name: u.name || u.display_name || u.email || u.id,
                    role: u.role || "staff",
                }));
            setStaffOptions(allStaff);

        } catch (err) {
            console.error("Failed to load sites/staff", err);
        }
    };

    // ==================================================
    // HANDLE CHANGE
    // ==================================================

    const handleChange = (e) => {
        const { name, value } = e.target;

        // asset_name change → auto-detect category + auto serial
        if (name === "asset_name") {
            const autoSerial      = `AST-${Date.now().toString().slice(-4)}`;
            const autoCategory    = detectCategory(value);
            setFormData(prev => ({
                ...prev,
                asset_name:     value,
                asset_category: autoCategory || prev.asset_category, // only override if detected
                serial_no:      prev.serial_no || autoSerial,
            }));
            return;
        }

        // all other fields (including asset_category) — just update directly
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // ==================================================
    // RESET FORM
    // ==================================================

    const resetForm = () => {
        setFormData({
            asset_name:          "",
            asset_category:      "",
            asset_specification: "",
            ownership_mode:      OWNERSHIP_MODE.RETURNABLE,
            serial_no:           "",
            current_holder_type: HOLDER_TYPES.STORE,
            current_holder_id:   "MAIN_STORE",
            remarks:             "",
            condition:           "WORKING",
        });
        setFromItemRequest(false);
    };

    // ==================================================
    // EDIT / ARCHIVE
    // ==================================================

    const handleEdit = (asset) => {
        setEditingAsset(asset);
        setFormData({
            asset_name:          asset.asset_name          || "",
            asset_category:      asset.asset_category      || "",
            asset_specification: asset.asset_specification || "",
            ownership_mode:      asset.ownership_mode      || OWNERSHIP_MODE.PERSONAL,
            serial_no:           asset.serial_no           || "",
            current_holder_type: asset.current_holder_type || HOLDER_TYPES.STORE,
            current_holder_id:   asset.current_holder_id   || "",
            remarks:             asset.remarks              || "",
            condition:           asset.condition           || "WORKING",
        });
    };

    const handleArchive = async (asset) => {
        if (!window.confirm(`Archive ${asset.asset_name}?`)) return;
        try {
            const { updateDoc, doc } = await import("firebase/firestore");
            const { db }             = await import("../../firebase");
            await updateDoc(doc(db, "asset_master", asset.id), {
                status:     "ARCHIVED",
                updated_at: new Date(),
            });
            setSuccess("Asset archived successfully");
            await loadAssets();
        } catch (err) {
            console.error(err);
            setError("Failed to archive asset");
        }
    };

    // ==================================================
    // CREATE / UPDATE ASSET
    // ==================================================

    const handleCreateAsset = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError("");
            setSuccess("");

            // UPDATE MODE
            if (editingAsset) {
    const prevCondition = editingAsset.condition || "WORKING";
    const newCondition  = formData.condition     || "WORKING";

    await updateAsset({
        asset_id:            editingAsset.id,
        asset_name:          formData.asset_name,
        asset_category:      formData.asset_category,
        asset_specification: formData.asset_specification,
        ownership_mode:      formData.ownership_mode,
        serial_no:           formData.serial_no,
        condition:           formData.condition,
        remarks:             formData.remarks,
        updated_by:          user?.uid || user?.id || "",
        company_id,
    });

    if (prevCondition !== newCondition &&
        (newCondition === "DAMAGED" || newCondition === "LOST")) {
        try {
            const { createNotification } = await import("../../services/notificationService");
            await createNotification({
                type:           "asset_condition_alert",
                title:          `⚠ Asset ${newCondition === "LOST" ? "Lost" : "Damaged"} — ${formData.asset_name}`,
                body:           `${formData.asset_name} marked as ${newCondition} by ${user?.name || "SM"}. Was: ${prevCondition}.`,
                route:          `/assets/ledger?asset_id=${editingAsset.id}`,
                for_roles:      ["owner", "sm"],
                company_id,
                reference_id:   editingAsset.id,
                reference_type: "asset",
                priority:       "high",
            });
        } catch (notifErr) {
            console.warn("[AssetMaster] condition notification failed:", notifErr);
        }
    }

    setSuccess("Asset updated successfully");
    setEditingAsset(null);

            } else {
                // CREATE MODE
                // ── Duplicate check — case-insensitive ──
                const duplicate = assets.find(
                    a => (a.asset_name || "").toLowerCase().trim() ===
                         (formData.asset_name || "").toLowerCase().trim()
                );
                if (duplicate) {
                    setError(`Asset "${duplicate.asset_name}" already exists in the Asset Registry. Please use the existing entry.`);
                    setSaving(false);
                    return;
                }

                let holder_name = "";

                if (formData.current_holder_type === HOLDER_TYPES.STORE) {
                    holder_name = formData.current_holder_id === "MAIN_STORE" ? "Main Store" : "Tool Room";
                }

                if (
                    formData.current_holder_type === HOLDER_TYPES.TECHNICIAN ||
                    formData.current_holder_type === "TECHNICIAN"
                ) {
                    const selectedStaff = staffOptions.find(u => u.id === formData.current_holder_id);
                    holder_name = selectedStaff?.name || "";
                }

                if (formData.current_holder_type === HOLDER_TYPES.SITE) {
                    const selectedSite = siteOptions.find(site => site.id === formData.current_holder_id);
                    holder_name = selectedSite?.site_name || "";
                }

                await createAsset({
                    ...formData,
                    holder_snapshot: createHolderSnapshot({
                        holderType: formData.current_holder_type,
                        holderId:   formData.current_holder_id,
                        collection: siteOptions,
                    }),
                    created_by: user?.uid || user?.id || "SYSTEM",
                    company_id,
                });

                setSuccess("Asset created successfully");
                setFromItemRequest(false);
            }

            resetForm();
            await loadAssets();

        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to save asset");
        } finally {
            setSaving(false);
        }
    };

    // ==================================================
    // UI
    // ==================================================

    return (
        <div style={{ padding: "20px" }}>

            {/* HEADER */}
            <div style={{ marginBottom: "20px" }}>
                <h2>Asset Master</h2>
                <p>HVAC Tool & Asset Registry</p>
            </div>

            {/* ── PRE-FILL BANNER (from Item Request) ── */}
            {fromItemRequest && (
                <div style={{
                    background: "#EFF6FF", border: "1px solid #BFDBFE",
                    borderRadius: 10, padding: "12px 16px", marginBottom: 16,
                    display: "flex", alignItems: "center", gap: 10,
                }}>
                    <span style={{ fontSize: 18 }}>📋</span>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#1D4ED8" }}>
                            Pre-filled from Item Request
                        </div>
                        <div style={{ fontSize: 12, color: "#3B82F6" }}>
                            Asset name has been filled in. Please select category, holder, and other details before saving.
                        </div>
                    </div>
                </div>
            )}

            {/* ERROR */}
            {error && (
                <div style={{ background: "#ffe5e5", color: "#d00000", padding: "12px", borderRadius: "8px", marginBottom: "16px" }}>
                    {error}
                </div>
            )}

            {/* SUCCESS */}
            {success && !editingAsset && (
                <div style={{ background: "#e7ffe7", color: "#008000", padding: "12px 16px", borderRadius: "8px", marginBottom: "16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                    <span>✅ {success}</span>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                        <button
                            onClick={() => navigate("/assets/transfer")}
                            style={{ padding:"6px 14px", background:"#2563eb", color:"#fff", border:"none", borderRadius:8, fontWeight:600, fontSize:12, cursor:"pointer" }}>
                            ↗ Transfer This Asset →
                        </button>
                        <button
                            onClick={() => { setSuccess(""); resetForm(); }}
                            style={{ padding:"6px 14px", background:"#16a34a", color:"#fff", border:"none", borderRadius:8, fontWeight:600, fontSize:12, cursor:"pointer" }}>
                            ➕ Add Another Asset →
                        </button>
                    </div>
                </div>
            )}
            {success && editingAsset && (
                <div style={{ background: "#e7ffe7", color: "#008000", padding: "12px", borderRadius: "8px", marginBottom: "16px" }}>
                    {success}
                </div>
            )}

            {/* CREATE / EDIT FORM */}
            <div style={{ background: "#ffffff", padding: "20px", borderRadius: "12px", marginBottom: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>

                <h3 style={{ marginBottom: "16px" }}>{editingAsset ? "Edit Asset" : "Create Asset"}</h3>

                <form onSubmit={handleCreateAsset}>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px" }}>

                        {/* ASSET NAME */}
                        <div>
                            <label>Asset Name</label>
                            <input
                                type="text" name="asset_name"
                                value={formData.asset_name}
                                onChange={handleChange}
                                placeholder="Enter asset name"
                                required style={inputStyle}
                            />
                        </div>

                        {/* CATEGORY */}
                        <div>
                            <label>Asset Category</label>
                            <select
                                name="asset_category"
                                value={formData.asset_category}
                                onChange={handleChange}
                                required style={inputStyle}>
                                <option value="">Select Category</option>
                                {Object.keys(ASSET_CATALOG).map(category => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                        </div>

                        {/* SPECIFICATION */}
                        <div>
                            <label>Specification</label>
                            <input
                                type="text" name="asset_specification"
                                value={formData.asset_specification}
                                onChange={handleChange}
                                placeholder="Optional" style={inputStyle}
                            />
                        </div>

                        {/* OWNERSHIP */}
                        <div>
                            <label>Ownership Mode</label>
                            <select
                                name="ownership_mode"
                                value={formData.ownership_mode}
                                onChange={handleChange}
                                style={inputStyle}>
                                <option value={OWNERSHIP_MODE.PERSONAL}>PERSONAL</option>
                                <option value={OWNERSHIP_MODE.RETURNABLE}>RETURNABLE</option>
                            </select>
                        </div>

                        {/* SERIAL */}
                        <div>
                            <label>Serial No</label>
                            <input
                                type="text" name="serial_no"
                                value={formData.serial_no}
                                readOnly
                                style={{ ...inputStyle, background: "#f3f4f6" }}
                            />
                        </div>

                        {/* HOLDER TYPE */}
<div>
    <label>Holder Type</label>
    {editingAsset ? (
        <input
            readOnly
            value={formData.current_holder_type}
            style={{ ...inputStyle, background: "#f3f4f6", color: "#6b7280" }}
        />
    ) : (
        <select
            name="current_holder_type"
            value={formData.current_holder_type}
            onChange={handleChange}
            style={inputStyle}>
            <option value={HOLDER_TYPES.STORE}>Store</option>
            <option value={HOLDER_TYPES.TECHNICIAN}>Staff / Technician</option>
            <option value={HOLDER_TYPES.SITE}>Site</option>
        </select>
    )}
</div>

{/* HOLDER ID */}
<div>
    <label>Current Holder</label>
    {editingAsset ? (
        <div>
            <input
                readOnly
                value={
                    formData.current_holder_type === HOLDER_TYPES.STORE
                        ? (formData.current_holder_id === "MAIN_STORE" ? "Main Store" : "Tool Room")
                        : staffOptions.find(u => u.id === formData.current_holder_id)?.name
                        || siteOptions.find(s => s.id === formData.current_holder_id)?.site_name
                        || formData.current_holder_id || "-"
                }
                style={{ ...inputStyle, background: "#f3f4f6", color: "#6b7280" }}
            />
            <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                To change holder — use Transfer Asset
            </p>
        </div>
    ) : (
        <select
            name="current_holder_id"
            value={formData.current_holder_id}
            onChange={handleChange}
            style={inputStyle}>
            {formData.current_holder_type === HOLDER_TYPES.STORE && (
                <>
                    <option value="">Select Store</option>
                    <option value="MAIN_STORE">Main Store</option>
                    <option value="TOOL_ROOM">Tool Room</option>
                </>
            )}
            {(formData.current_holder_type === "TECHNICIAN" ||
              formData.current_holder_type === HOLDER_TYPES.TECHNICIAN) && (
                <>
                    <option value="">
                        {staffOptions.length === 0 ? "Loading staff..." : "Select Staff"}
                    </option>
                    {staffOptions.map(u => (
                        <option key={u.id} value={u.id}>
                            {u.name} ({u.role})
                        </option>
                    ))}
                </>
            )}
            {formData.current_holder_type === HOLDER_TYPES.SITE && (
                <>
                    <option value="">Select Site</option>
                    {siteOptions.map(site => (
                        <option key={site.id} value={site.id}>
                            {site.site_name}
                        </option>
                    ))}
                </>
            )}
        </select>
    )}
</div>

                        {/* CONDITION */}
                        <div>
                            <label>Condition</label>
                            <select
                                name="condition"
                                value={formData.condition}
                                onChange={handleChange}
                                style={inputStyle}>
                                <option value="WORKING">Working</option>
                                <option value="IN_REPAIR">In Repair</option>
                                <option value="DAMAGED">Damaged</option>
                                <option value="LOST">Lost</option>
                            </select>
                            {editingAsset &&
                             (formData.condition === "DAMAGED" || formData.condition === "LOST") &&
                             formData.condition !== (editingAsset.condition || "WORKING") && (
                                <p style={{ fontSize: "12px", color: "#dc2626", marginTop: "4px" }}>
                                    ⚠ Owner will be notified of this condition change
                                </p>
                            )}
                        </div>

                    </div>

                    {/* REMARKS */}
                    <div style={{ marginTop: "16px" }}>
                        <label>Remarks</label>
                        <textarea
                            placeholder="Optional remarks"
                            name="remarks"
                            value={formData.remarks}
                            onChange={handleChange}
                            rows="3"
                            style={{ ...inputStyle, resize: "vertical" }}
                        />
                    </div>

                    {/* SUBMIT */}
                    <div style={{ display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
                        <button
                            type="submit" disabled={saving}
                            style={{
                                background: "#1e40af", color: "#ffffff",
                                border: "none", padding: "12px 20px",
                                borderRadius: "8px", cursor: "pointer", fontWeight: "600",
                            }}>
                            {saving ? "Saving..." : editingAsset ? "Update Asset" : "Create Asset"}
                        </button>

                        {(editingAsset || fromItemRequest) && (
                            <button
                                type="button"
                                onClick={() => { setEditingAsset(null); resetForm(); }}
                                style={{
                                    background: "#e5e7eb", color: "#374151",
                                    border: "none", padding: "12px 20px",
                                    borderRadius: "8px", cursor: "pointer", fontWeight: "600",
                                }}>
                                {editingAsset ? "Cancel Edit" : "Clear Form"}
                            </button>
                        )}
                    </div>

                </form>
            </div>

            {/* ASSET LIST */}
            <div style={{ background: "#ffffff", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>

                <h3 style={{ marginBottom: "16px" }}>Asset List</h3>

                {loading ? (
                    <p>Loading assets...</p>
                ) : assets.length === 0 ? (
                    <p>No assets found</p>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table width="100%" border="1" cellPadding="10" style={{ borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th>Asset Name</th>
                                    <th>Specification</th>
                                    <th>Category</th>
                                    <th>Ownership</th>
                                    <th>Holder</th>
                                    <th>Serial</th>
                                    <th>Condition</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {assets.map(asset => (
                                    <tr key={asset.id}>
                                        <td>{asset.asset_name}</td>
                                        <td>{asset.asset_specification || "-"}</td>
                                        <td>{asset.asset_category}</td>
                                        <td>{asset.ownership_mode}</td>
                                        <td>
                                            {asset.current_holder_type || "-"} /{" "}
                                            {getResolvedHolderLabel({ asset, collection: siteOptions }) || "-"}
                                        </td>
                                        <td>{asset.serial_no || "-"}</td>
                                        <td>
                                            {(() => {
                                                const c  = asset.condition || "WORKING";
                                                const ui = {
                                                    WORKING:   { label: "Working",   bg: "#d1fae5", color: "#065f46" },
                                                    IN_REPAIR: { label: "In Repair", bg: "#fef3c7", color: "#92400e" },
                                                    DAMAGED:   { label: "Damaged",   bg: "#fee2e2", color: "#991b1b" },
                                                    LOST:      { label: "Lost",      bg: "#f3f4f6", color: "#374151" },
                                                }[c] || { label: c, bg: "#f3f4f6", color: "#374151" };
                                                return (
                                                    <span style={{
                                                        background: ui.bg, color: ui.color,
                                                        padding: "3px 10px", borderRadius: "999px",
                                                        fontSize: "12px", fontWeight: "600",
                                                    }}>
                                                        {ui.label}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td>{asset.status}</td>
                                        <td style={{ display: "flex", gap: "8px" }}>
                                            <button onClick={() => handleEdit(asset)} className="btn btn-primary btn-sm">Edit</button>
                                            <button onClick={() => handleArchive(asset)} className="btn btn-danger btn-sm">Archive</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

        </div>
    );
}

const inputStyle = {
    width:        "100%",
    padding:      "10px",
    marginTop:    "6px",
    borderRadius: "8px",
    border:       "1px solid #d1d5db",
    boxSizing:    "border-box",
};
